// The copy verdicts a locale bundle is held to, ONE implementation each: the
// guards that run at test time (`apps/marketing/src/copy.dashes.test.ts`, the en/es
// parity blocks in both apps) and the post-edit hook (`scripts/copy-lint.ts`) call
// these, so the fast check and the gate cannot disagree about what a violation is.
import type { Catalog } from './catalog';

/**
 * The em dash is the most recognisable tell of generated copy (#1407). The en dash
 * stays legal: it is the range glyph ("$100 – $1000"), not prose punctuation.
 */
export const EM_DASH = '—';

/** Every line of `text` carrying an em dash: 1-based line number and the trimmed line. */
export function emDashLines(text: string): { line: number; text: string }[] {
  return text
    .split('\n')
    .flatMap((line, i) => (line.includes(EM_DASH) ? [{ line: i + 1, text: line.trim() }] : []));
}

/**
 * REFUSED punctuation forms, each mapped to the halfwidth form that replaces it.
 *
 * Named for what it does rather than for the Unicode block its keys come from:
 * the curly quotes are U+201C/U+201D, not U+FFxx, and a map called "fullwidth"
 * that carries them would be lying about its own membership rule. The rule is
 * "a halfwidth equivalent exists and reads correctly in every locale we ship".
 *
 * Halfwidth is the convention, and it was decided on a bundle that had drifted
 * into carrying both. Measured on `zh.json` when the rule was written: 863
 * halfwidth commas against 226 fullwidth, 141 colons against 37 — the split
 * falling cleanly between translation batches rather than between sentences, so
 * it was a convention nobody had written down rather than a judgement anyone had
 * made per string.
 *
 * THE DELIBERATELY ABSENT SET, enumerated so a future sweep cannot "finish the
 * job", measured on `zh.json` 2026-09-25:
 *
 *   - `。` (1,406) and `、` (552). Neither has a halfwidth form that reads
 *     correctly in Chinese: a CJK sentence ends in `。`, and `、` separates list
 *     items with no ASCII equivalent at all. The bundle agrees — across every
 *     string, ASCII `.` is used as a sentence end after a CJK character exactly
 *     zero times. Converting them would be a typographic error applied 1,958
 *     times, not consistency.
 *   - `《》` (2 pairs). Title marks, the Chinese equivalent of italicising a
 *     work's name — `《职业展望手册》` is a real publication. ASCII quotes around a
 *     title say something different, so there is nothing to map them to.
 *   - `…` (1). It is NOT a zh drift: the same key, `contact.form.sending`, carries
 *     it in `en` and `es` too, so it is this repo's house style for a pending
 *     state rather than a locale forgetting the rule. A one-locale verdict has no
 *     business refusing a three-locale decision.
 *
 * `“` and `”` are IN, added 2026-09-25 (#4976): 6 pairs in `zh.json`, all marking
 * a quoted phrase, and `"` says exactly the same thing. They were invisible to
 * this verdict until CodeRabbit caught a pair by hand on #4972 — the guard was
 * narrower than the rule `apps/marketing/CLAUDE.md` states, which is the shape
 * that makes a guard QUIET rather than wrong.
 */
export const FULLWIDTH_PUNCTUATION: Readonly<Record<string, string>> = {
  '，': ',',
  '：': ':',
  '；': ';',
  '！': '!',
  '？': '?',
  '（': '(',
  '）': ')',
  '“': '"',
  '”': '"',
};

const FULLWIDTH = new RegExp(`[${Object.keys(FULLWIDTH_PUNCTUATION).join('')}]`);

/** Every line of `text` carrying a fullwidth form from {@link FULLWIDTH_PUNCTUATION}. */
export function fullwidthPunctuationLines(text: string): { line: number; text: string }[] {
  return text
    .split('\n')
    .flatMap((line, i) => (FULLWIDTH.test(line) ? [{ line: i + 1, text: line.trim() }] : []));
}

/**
 * Forbidden marketing-copy terms per the claims row-69 owner ruling (2026-09-18):
 * the product is not sold as running without supervision, in any shipped locale.
 * Terms are matched case-insensitively; key NAMES are not copy and are never
 * matched — the guard scans VALUES only.
 */
export const ROW69_FORBIDDEN_WORDS: Readonly<Record<'en' | 'es' | 'zh', readonly string[]>> = {
  en: ['unattended', 'no supervision', 'no humans'],
  es: ['sin supervisión', 'sin humanos', 'no supervisado'],
  // '无需人工介入' ("no human intervention needed"): owner ruling 18-S2, 2026-10-08.
  zh: ['无人值守', '无人监督', '无人盯着', '无需人工介入'],
};

const ROW69 = new RegExp(
  Object.values(ROW69_FORBIDDEN_WORDS)
    .flat()
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'i',
);

/** Every line of `text` carrying a row-69 forbidden word, case-insensitively. */
export function forbiddenCopyLines(text: string): { line: number; text: string }[] {
  return text
    .split('\n')
    .flatMap((line, i) => (ROW69.test(line) ? [{ line: i + 1, text: line.trim() }] : []));
}

/** The keys of `from` that `into` does not carry as its own, sorted. */
export function missingKeys(from: Catalog, into: Catalog): string[] {
  return Object.keys(from)
    .filter((key) => !Object.hasOwn(into, key))
    .sort();
}
